import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BookingService, BookingDetailsDto } from '@/services/booking.service';
import { logger } from '@/utils/logger';
import { dashboardQueryOptions } from '@/config/react-query.config';
import { triggerNativeRemoveFromCalendar } from '@/utils/nativeBridge';

export const useTodayBookingUsage = (locationId: string, facility: string, date: string, excludeBookingId?: string) => {
  return useQuery<{ minutesBooked: number; bookingCount: number }>({
    queryKey: ['booking-today-usage', locationId, facility, date, excludeBookingId ?? ''],
    queryFn: () => BookingService.getTodayUsage(facility, date, locationId, excludeBookingId),
    enabled: !!(locationId && facility && date),
    staleTime: 0,
  });
};

export const useBookings = () => {
  const queryClient = useQueryClient();

  const {
    data: bookings,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery<BookingDetailsDto[]>({
    queryKey: ['bookings'],
    queryFn: () => BookingService.getAllBookingsForDashboard(),
    ...dashboardQueryOptions,
    /* BOOKINGS GO STALE THE MOMENT YOU LOOK AWAY, AND THE SERVER CHANGES THEM
       WITHOUT US.

       `dashboardQueryOptions` caches for five minutes, which made
       `refetchOnMount: true` a no-op — React Query only refetches on mount
       when the data is already stale, so coming back to Home inside that
       window re-rendered the cache and nothing was requested. An unpaid
       drop-in that the checkout expiry sweep has since released kept showing
       on the active booking card until the app was killed and reopened, or
       the desktop page was hard-reloaded.

       These bookings are not ours alone to invalidate: the expiry sweep, a
       cancellation taken at the desk, and a reschedule made elsewhere all
       change them server-side with nothing to tell the client. Asking again
       on every mount and every return to the foreground is the only way the
       screen can be trusted. */
    staleTime: 0,
    refetchOnMount: 'always' as const,
    refetchOnWindowFocus: true,
  });

  const [cancellingBookingId, setCancellingBookingId] = useState<string | null>(null);

  const cancelBookingMutation = useMutation({
    mutationFn: (bookingId: string) => BookingService.cancelBooking(bookingId),
    onSuccess: (_, bookingId) => {
      // Remove it from the cache immediately so it disappears without waiting
      // on a refetch (which can lag behind a not-yet-consistent backend read).
      queryClient.setQueryData<BookingDetailsDto[]>(['bookings'], (old) =>
        old ? old.filter((b) => (b._id || b.id) !== bookingId) : old
      );
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      setCancellingBookingId(null);
      // Same uid format used when the booking was (maybe) added to the
      // calendar — native no-ops gracefully if it never created one.
      triggerNativeRemoveFromCalendar(`${bookingId}@teebox`);
    },
    onError: (error) => {
      logger.error('Failed to cancel booking:', error);
      setCancellingBookingId(null);
    },
  });

  const handleCancelBooking = async (bookingId: string) => {
    setCancellingBookingId(bookingId);
    try {
      await cancelBookingMutation.mutateAsync(bookingId);
      // Success is handled by the mutation's onSuccess callback
    } catch (error) {
      // Error is handled by the mutation's onError callback
      logger.error('Error cancelling booking:', error);
    }
  };

  const handleCheckInBooking = async (
    bookingId: string,
    onSuccess?: () => void
  ) => {
    try {
      await checkInBookingMutation.mutateAsync(bookingId);
      // Call the onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      // Error is handled by the mutation's onError callback
      logger.error('Error checking in:', error);
    }
  };

  const checkInBookingMutation = useMutation({
    mutationFn: (bookingId: string) => BookingService.checkInBooking(bookingId),
    onSuccess: (result) => {
      // Start session timer using the booking's actual endTime so late check-ins
      // count down from the real remaining time, not the full session duration.
      if (result?.endTime) {
        const endTimestamp = new Date(result.endTime).getTime();
        import('@/utils/sessionTimer').then(({ sessionTimer }) => {
          sessionTimer.startSession(
            result._id || result.bookingId,
            endTimestamp,
            result.facility || 'Unknown',
            result.location?.name || 'Unknown Location'
          );
        });
      }

      // Invalidate and refetch bookings to show updated status
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
    onError: (error) => {
      logger.error('Failed to check in:', error);
    },
  });

  return {
    bookings,
    isLoading,
    error,
    refetch,
    cancelBooking: handleCancelBooking,
    checkInBooking: handleCheckInBooking,
    cancellingBookingId,
    isRefreshing: isFetching && !isLoading,
    isCheckingIn: checkInBookingMutation.isPending,
  };
};
